import React, {
	PropsWithChildren,
	memo,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useState,
} from "react";
import { LatLngLiteral } from "leaflet";
import { cloneDeep, isString } from "lodash";
import { Option } from "uikit";

import { Map } from "../../../../../../services";
import { useMapSearch } from "../../../../../../hooks";
import { MapSearch } from "../../../../../Orders";
import { Popup } from "../../../../../common";
import PointOrderModalTableBase from "../..";
import {
	HandleEventsProps,
	useHandleEvents,
	ValueAccessors,
	useGetCoordinatesForSettlement,
} from "../../hooks";
import cityToString from "../utils/cityToString";
import { StyledInput, StyledPopup } from "../styled";
import { OverlaySettlement } from "../Overlays";
import { cleanSettlementText } from "../utils/normolize";

const SettlementBase: React.FC<SettlementBase.Props> = ({
	rowData,
	rowIndex,
	dataKey,
	colIndex,
	setData,
	styles,
	offset,
	// customerId,
	height,
	handleEventsOptions,
	containerId,
	addSettlement,
	addSettlementCoordinates,
	countRow,
	setReloadCell,
	reloadCell,
	street,
}) => {
	const idCel = useMemo(() => rowData.raw.id, [rowData.raw.id]);
	const isLastRow = useMemo(() => idCel === countRow, [countRow, idCel]);

	const { get: getSettlementCoordinates } = useGetCoordinatesForSettlement();

	const {
		inputValue,
		open,
		query,
		save,
		setSave,
		onChangeInputValue,
		onEnterToInput,
		onExistFromInput,
		onClickToInput,
		onFocusToInput,
		onBlurCaptureToInput,
		setColIndex,
		setDataKey,
		setInputId,
		setRowId,
		setRowIndex,
		nextElementById,
		language,
		reset,
		allowType,
		setAllowType,
		setLoading,
		setInputValue,
		setOpen,
		loading,
		nextFocus,
	} = useHandleEvents(
		{
			idCel,
			isLastRow,
		},
		handleEventsOptions,
	);

	const [isNewInput, setIsNewInput] = useState<boolean>(false);
	const [isQuery, setIsQuery] = useState<boolean>(false);
	const [isSelect, setIsSelect] = useState<boolean>(false);
	const [activeId, setActiveOption] = useState<number>(-1);
	const [result, setResult] = useState<MapSearch.Result>({
		loading: true,
	});

	useLayoutEffect(() => {
		setColIndex(`${colIndex}`);
		setDataKey(dataKey);
		setInputId(`${idCel + dataKey}`);
		setRowId(idCel);
		setRowIndex(`${rowIndex}`);
		addSettlement(rowData.raw as unknown as Map.Search.Object, idCel);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!rowData.raw.type) {
			setIsQuery(false);
			setResult({ loading: true });
		}
	}, [rowData]);

	useEffect(() => {
		if (isString(query) && allowType === "reset") {
			setResult({ loading: true });
			addSettlement(undefined, idCel);
			setAllowType(null);
			setIsQuery(false);
		}
	}, [addSettlement, allowType, query, idCel, setAllowType]);

	const querySearch = useMemo(() => {
		const list: (string | null)[] = ["query"];
		if (list.includes(allowType) && isString(query) && query?.length > 0) {
			setAllowType("reset");
		}
		return query || "";
	}, [allowType, query, setAllowType]);

	const updatedData = useCallback(
		(params: Map.Search.Object) => {
			addSettlement(params, idCel);
			const city = cityToString(params);
			const oldCoordinates = `${rowData.raw?.coordinates?.lat}${rowData.raw?.coordinates?.lng}`;
			const newData = cloneDeep(rowData);
			// base
			newData.city = city;
			newData.type = params.type;
			newData.coordinates = params.coordinates;
			newData.save = true;
			newData.saveType = "city";
			newData.raw.save = false;
			newData.raw.saveType = "city";

			// raw
			newData.raw.type = params.type;
			newData.raw.coordinates = params.coordinates;
			newData.raw.country = params.country;
			newData.raw.countryCode = params.countryCode;
			newData.raw.settlement = params.settlement || "";
			newData.raw.settlementType = params.settlementType || "";

			newData.raw.region = params.region || "";
			newData.raw.district = params.district || "";
			// clear
			newData.entrance = "";
			newData.house = "";
			newData.polygon = "";
			newData.flat = "";
			newData.street = "";
			// clear raw
			newData.raw.customHouse = "";
			newData.raw.customStreet = "";
			newData.raw.entrance = "";
			newData.raw.flat = "";
			newData.raw.house = "";
			newData.raw.name = "";
			newData.raw.street = "";
			newData.raw.streetType = "";
			newData.raw.sector = undefined;
			// reset
			const newCoordinates = `${params.coordinates?.lat}${params.coordinates?.lng}`;
			if (
				cleanSettlementText(rowData.raw.settlement) ===
					cleanSettlementText(newData.raw.settlement) &&
				rowData.raw.settlementType === newData.raw.settlementType &&
				oldCoordinates === newCoordinates
			) {
				if (handleEventsOptions?.nextElement) {
					nextElementById(handleEventsOptions?.nextElement);
				}
				if (!loading) reset();
				setLoading(false);
				setResult({ loading: true });
				setIsQuery(false);
				return;
			}

			setData(newData);
			setLoading(false);
			setTimeout(() => {
				setReloadCell(`${idCel}settlement`);
			}, 10);
			reset();
			if (handleEventsOptions?.nextElement) {
				nextElementById(handleEventsOptions?.nextElement);
			}
			setResult({ loading: true });
			setIsQuery(false);
		},

		[
			addSettlement,
			handleEventsOptions?.nextElement,
			idCel,
			nextElementById,
			reset,
			rowData,
			setData,
			setReloadCell,
			setLoading,
			loading,
		],
	);

	const updatedDataClear = useCallback(
		(str: string) => {
			const newData = cloneDeep(rowData);

			if (!newData.raw?.settlement) return;
			if (str) return;
			// base
			newData.save = true;
			newData.saveType = "city";
			newData.raw.save = false;
			newData.raw.saveType = "city";
			newData.raw.type = "";
			newData.raw.country = "";
			newData.raw.countryCode = "";
			newData.raw.settlement = "";
			newData.raw.settlementType = "";
			newData.raw.region = "";
			newData.raw.district = "";
			// clear
			newData.entrance = "";
			newData.house = "";
			newData.polygon = "";
			newData.flat = "";
			newData.street = "";
			// clear raw
			newData.raw.customHouse = "";
			newData.raw.customStreet = "";
			newData.raw.entrance = "";
			newData.raw.flat = "";
			newData.raw.house = "";
			newData.raw.name = "";
			newData.raw.street = "";
			newData.raw.streetType = "";
			newData.raw.sector = undefined;
			// reset

			setTimeout(() => {
				setReloadCell(`${idCel}settlement`);
			}, 10);
			setData(newData);
			addSettlement(undefined, idCel);
		},
		[addSettlement, idCel, rowData, setData, setReloadCell],
	);

	useEffect(() => {
		if (inputValue && !isNewInput && !isSelect) {
			setIsSelect(false);
			setIsNewInput(true);
		}
		if (inputValue && isNewInput && isSelect) {
			setIsNewInput(false);
		}
		if (reloadCell === `${idCel}house`) {
			setIsSelect(false);
			setIsNewInput(false);
		}
	}, [idCel, inputValue, isNewInput, isSelect, reloadCell]);

	useEffect(() => {
		if (reloadCell === `${idCel}house`) {
			setTimeout(() => {
				setInputValue(null);
				reset();
				setSave(false);
				setIsQuery(false);
			}, 0);
		}
	}, [reloadCell, idCel, setSave, setInputValue, reset]);

	const openOverlay = useMemo(() => open, [open]);

	const mapSearchOptions = useMemo<MapSearch.Options>(
		() => ({
			language,
			searchType: "settlement",
			// customerId
			// searchType: customerId ? ["settlement", "favorite"] : "settlement",
			country: "ua",
			query: querySearch ?? "",
			lite: true,
		}),
		[language, querySearch],
	);
	const mapSearchResult = useMapSearch(mapSearchOptions);

	useLayoutEffect(() => {
		if (mapSearchResult.loading) return;
		setResult(mapSearchResult);
	}, [mapSearchResult]);

	useEffect(() => {
		if (result.loading) {
			setLoading(true);

			return;
		}
		if (result.objects?.length) {
			if (loading) setOpen(true);
		}
	}, [result, setLoading, loading, setOpen]);

	useEffect(() => {
		if (result.loading) return;
		// if (result.objects?.length) setActiveOption(0);
		if (result.objects?.length) {
			setActiveOption(-1);
			setIsQuery(false);
		}
	}, [result]);

	useLayoutEffect(() => {
		if (
			!open &&
			save &&
			!rowData.save &&
			!rowData.raw.save &&
			activeId === -1 &&
			!rowData.raw.street
		) {
			const newData = cloneDeep(rowData);

			if (newData.raw.coordinates?.lat && newData.raw.coordinates?.lng) {
				addSettlementCoordinates(
					newData.raw.coordinates,
					newData.raw.id,
				);
			}

			newData.save = true;
			newData.saveType = "settlement";
			newData.raw.save = false;
			newData.raw.saveType = "settlement";
			newData.type = "settlement";
			newData.raw.type = "settlement";

			if (street) {
				newData.coordinates = street.coordinates;
				newData.raw.coordinates = street.coordinates;
			}
			setData(newData);
			setSave(false);
		}
	}, [
		open,
		save,
		rowData,
		activeId,
		addSettlementCoordinates,
		setData,
		setSave,
		street,
	]);

	const onSelect = useCallback(
		async (option: Option<Map.Search.Object>) => {
			if (option) {
				setIsSelect(true);
				let params = option.value;
				const settlementQuery =
					params.settlement || (params as any).name || "";
				const { searchType } = params as any;
				if (
					settlementQuery &&
					(params as any).placeId &&
					(searchType === "google_geocode" ||
						searchType === "osm_nominatim")
				) {
					const enriched = await getSettlementCoordinates(
						params as any,
						settlementQuery,
					);
					if (enriched) params = enriched;
				}
				updatedData(params);
				setIsNewInput(false);
				setInputValue(null);
			}
			if (!option && Array.isArray(result) && result.length) {
				const first = result.at(0);
				setIsSelect(true);
				let params = first as Map.Search.Object;
				const settlementQuery =
					params.settlement || (params as any).name || "";
				const { searchType } = params as any;
				if (
					settlementQuery &&
					(params as any).placeId &&
					(searchType === "google_geocode" ||
						searchType === "osm_nominatim")
				) {
					const enriched = await getSettlementCoordinates(
						params as any,
						settlementQuery,
					);
					if (enriched) params = enriched;
				}
				updatedData(params);
				setIsNewInput(false);
				setInputValue(null);
			}
		},
		[getSettlementCoordinates, result, setInputValue, updatedData],
	);

	const propsInput = useMemo(() => {
		const value = `${
			rowData.raw?.type || open ? cityToString(rowData.raw) : ""
		}`;
		return {
			value: inputValue !== null ? inputValue : value,
		};
	}, [inputValue, open, rowData.raw]);

	const placeholder = useMemo(
		() => `${rowData[dataKey] || ""}`,
		[dataKey, rowData],
	);

	const size = useMemo(
		() =>
			propsInput.value.length > 5 ? propsInput.value.length * 1.25 : 5,
		[propsInput.value.length],
	);

	return (
		<StyledPopup
			useClickControl
			useArrow={false}
			open={openOverlay}
			offset={offset}
			styles={styles}
			trackerId={dataKey + idCel}
			containerId={containerId}
			tracker={
				<StyledInput
					size={size}
					height={height}
					b={isNewInput ? "1px solid red" : ""}
					id={`${idCel + dataKey}`}
					tabIndex={-1}
					aria-label={dataKey}
					aria-rowindex={rowIndex}
					aria-colindex={colIndex}
					autoComplete="one-time-code"
					onChange={(event) => {
						setResult({ loading: true });
						const { target } = event;
						const { value } = target;
						if (!value) updatedDataClear(value);
						setLoading(true);
						setIsNewInput(true);
						onChangeInputValue(event);
						setIsSelect(false);
						setIsQuery(true);
					}}
					onKeyUp={(event) => {
						const keyEvent = event.key;
						const list: string[] = ["Tab", "Enter"];
						if (
							activeId === -1 &&
							list.includes(keyEvent) &&
							!mapSearchResult?.loading
						) {
							return;
						}
						if (isQuery) {
							const list2 = [
								"ArrowLeft",
								"ArrowRight",
								"Tab",
								"Enter",
							];
							if (list2.includes(keyEvent)) {
								event.stopPropagation();
								event.preventDefault();
								return;
							}
						}

						onEnterToInput(event);
					}}
					onKeyDown={(event) => {
						const keyEvent = event.key;
						if (activeId === -1 && !mapSearchResult?.loading) {
							return;
						}

						if (
							activeId >= 0 &&
							!mapSearchResult?.loading &&
							!isSelect
						) {
							const list = ["ArrowLeft", "ArrowRight"];
							if (list.includes(keyEvent)) {
								return;
							}
						}

						if (isQuery) {
							const list2 = [
								"ArrowLeft",
								"ArrowRight",
								"Tab",
								"Enter",
							];
							if (list2.includes(keyEvent)) {
								event.stopPropagation();
								event.preventDefault();
								return;
							}
						}
						if (mapSearchResult?.loading) {
							const list = ["ArrowDown", "ArrowUp"];
							if (list.includes(keyEvent)) {
								event.stopPropagation();
								event.preventDefault();
								return;
							}
						}

						const list: string[] = ["Tab", "Enter"];

						if (
							list.includes(keyEvent) &&
							mapSearchResult?.loading
						) {
							nextFocus();
							event.stopPropagation();
							event.preventDefault();
							return;
						}

						onExistFromInput(event);
					}}
					onClick={onClickToInput}
					onBlurCapture={onBlurCaptureToInput}
					placeholder={placeholder}
					onFocus={onFocusToInput}
					onCut={(event) => {
						event.preventDefault();
					}}
					onDragOver={(event) => {
						event.preventDefault();
					}}
					onDrop={(event) => {
						event.preventDefault();
					}}
					{...propsInput}
				/>
			}
		>
			<OverlaySettlement
				data={
					isSelect
						? {
								loading: true,
						  }
						: result
				}
				activeId={activeId}
				setActiveOption={setActiveOption}
				onSelect={onSelect}
				query={inputValue || ""}
			/>
		</StyledPopup>
	);
};

declare namespace SettlementBase {
	interface Props extends PropsWithChildren {
		rowData: PointOrderModalTableBase.OrderModal;
		setData: (data: PointOrderModalTableBase.OrderModal) => void;
		reset?: (id: number) => void;
		addSettlement: (
			settlement: Map.Search.Object | undefined,
			id: number,
		) => void;
		addSettlementCoordinates: (
			settlement: LatLngLiteral | undefined,
			id: number,
		) => void;
		rowIndex?: number;
		dataKey: ValueAccessors | string;
		colIndex: number;
		height?: number;
		styles?: Popup.Styles;
		offset?: Popup.Props["offset"];
		customerId?: number;
		handleEventsOptions?: HandleEventsProps;
		containerId?: string;
		countRow?: number;
		reloadCell: string;
		setReloadCell: React.Dispatch<React.SetStateAction<string>>;
		loadingChangePoint: boolean;
		/** Street with house to selection */
		street?: Map.Search.Object;
		setLoadingChangePoint: React.Dispatch<React.SetStateAction<boolean>>;
	}
}

export const Settlement = memo(SettlementBase);

export default SettlementBase;
